import classNames from 'classnames'
import {
  createContext,
  forwardRef,
  type MutableRefObject,
  type PropsWithChildren,
  type ReactElement,
  useRef
} from 'react'
import { Button, Modal, type ModalProps } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import styles from '../assets/scss/MaModal.module.scss'

export const ModalDialogContext = createContext<HTMLElement | undefined>(
  undefined
)

export interface MaModalRef {
  dialog?: HTMLElement
}

function ModalDialogProvider ({
  dialog,
  children
}: Readonly<MaModalRef & PropsWithChildren>): ReactElement {
  return (
    <ModalDialogContext.Provider value={dialog}>
      {children}
    </ModalDialogContext.Provider>
  )
}

interface MaModalProps extends ModalProps, PropsWithChildren {
  readonly footer?: ReactElement
}

const MaModal = forwardRef<MaModalRef | undefined, MaModalProps>(
  function MaModal (
    {
      show,
      title,
      header,
      footer,
      children,
      className,
      onClose,
      onShow,
      ...props
    }: MaModalProps,
    forwardedRef
  ): ReactElement {
    const { t } = useTranslation()
    const modalRef = useRef<MaModalRef>()
    const ref = forwardedRef ?? modalRef
    const dialog = (ref as MutableRefObject<MaModalRef | undefined>).current
      ?.dialog

    return (
      <ModalDialogProvider dialog={dialog}>
        <Modal
          ref={ref}
          show={show}
          onShow={onShow}
          onHide={onClose}
          className={classNames(styles['ma-modal'], className)}
          {...props}
        >
          {header || (
            <Modal.Header
              closeButton
              closeLabel={t('common.button.labels.close')}
            >
              <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
          )}
          <Modal.Body>{children}</Modal.Body>
          <Modal.Footer>
            {footer || (
              <Button variant="primary" onClick={onClose}>
                {t('common.button.labels.close')}
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </ModalDialogProvider>
    )
  }
)

export default MaModal
